import { InternalIcon, Link, Stack, Tile, Typography, createLucideIcon, useTheme } from "@carrotfertility/carotene-core"
import { IconLinkSkeleton, ParagraphSkeleton, Skeleton, TextSkeleton } from "@internal/ui"
import { Calendar } from "lucide-react"
import React from "react"
import { useIntl } from "react-intl"
import { AppointmentsAvatar, AppointmentsAvatarSkeleton } from "./AppointmentsAvatar"
import {
  formatAppointmentDateWithYear,
  formatAppointmentTimeWithZone,
  formatProviderName,
  formatStartingIn,
  isSameLocalDay
} from "./formatting"
import { useAppointments } from "./useAppointments"
import { useAppointmentTiming } from "./useAppointmentTiming"
import type { UpcomingAppointment } from "./types"
import { Link as ReactRouterLink } from "react-router-dom"

const CalendarIcon = createLucideIcon(Calendar, "CalendarIcon")

const VIEW_ALL_LABEL = "View all appointments"

function TileShell({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Tile variant="secondary" color="primary">
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>{children}</Stack>
    </Tile>
  )
}

function Heading(): JSX.Element {
  return (
    <Typography variant="h5" component="h2" color="text.primary">
      Preparing for
    </Typography>
  )
}

function ViewAllLink({ appointmentsPath }: { appointmentsPath: string }): JSX.Element {
  return (
    <Link
      component={ReactRouterLink}
      to={appointmentsPath}
      underline="hover"
      endIcon={<InternalIcon color="primary" fontSize="small" />}
      sx={{ alignSelf: "flex-start" }}
    >
      {VIEW_ALL_LABEL}
    </Link>
  )
}

function UpcomingBody({
  appointment,
  appointmentsPath
}: {
  appointment: UpcomingAppointment
  appointmentsPath: string
}): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { now, imminent } = useAppointmentTiming(appointment)

  const { provider, date } = appointment
  // A wider window than imminence: a 5pm visit reads "Today" all morning without yet offering Join.
  const dateLabel = isSameLocalDay(date, now) ? "Today" : formatAppointmentDateWithYear(intl, date)

  return (
    <>
      <Heading />
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.sm)}>
        <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
        <Typography variant="h6" component="p" color="text.primary">
          {formatProviderName(provider)}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <CalendarIcon fontSize="small" aria-hidden />
        <Typography variant="body2">{`${dateLabel} · ${formatAppointmentTimeWithZone(intl, date)}`}</Typography>
      </Stack>
      {imminent && (
        // `color` after `typography`, which spreads a role carrying its own.
        <Typography sx={{ typography: "chip", color: theme.palette.primary.main }}>
          {`Starting ${formatStartingIn(intl, date, now)}`}
        </Typography>
      )}
      <ViewAllLink appointmentsPath={appointmentsPath} />
    </>
  )
}

// Stand-ins for the loaded tile's data, laid out transparent so the frame wraps like a typical tile
// when the column narrows. Mid-length on purpose: an unusually long or short date or name can still
// land a line off.
const SAMPLE_PROVIDER = "Dr. Jordan Rivera, MD"
const SAMPLE_DATE = new Date(2026, 9, 15, 14, 30)

function TileSkeleton(): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const sampleWhen = `${formatAppointmentDateWithYear(intl, SAMPLE_DATE)} · ${formatAppointmentTimeWithZone(
    intl,
    SAMPLE_DATE
  )}`
  return (
    <TileShell>
      <Stack role="status" aria-label="Loading your appointments" spacing={theme.spacing(theme.tokens.spacing.md)}>
        <TextSkeleton variant="h5" width={128} />
        <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.sm)}>
          <AppointmentsAvatarSkeleton />
          <ParagraphSkeleton variant="h6" text={SAMPLE_PROVIDER} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.xs)}>
          {/* The calendar icon's `small` box. */}
          <Skeleton width="1.25rem" height="1.25rem" />
          <ParagraphSkeleton variant="body2" text={sampleWhen} />
        </Stack>
        <IconLinkSkeleton label={VIEW_ALL_LABEL} />
      </Stack>
    </TileShell>
  )
}

export function SidebarAppointmentsTile({ appointmentsPath }: { appointmentsPath: string }): JSX.Element | null {
  const { data, isLoading, isError } = useAppointments()

  if (isError) {
    return null
  }
  if (isLoading || !data) {
    return <TileSkeleton />
  }

  const upcoming = data.upcoming.find((appointment) => appointment.status === "scheduled")
  if (!upcoming) {
    return null
  }

  return (
    <TileShell>
      <UpcomingBody appointment={upcoming} appointmentsPath={appointmentsPath} />
    </TileShell>
  )
}
